import {
  Component, Input, ViewChildren, ViewChild, QueryList, ElementRef, AfterViewInit, NgZone,
  OnInit
} from '@angular/core';
import { AgmInfoWindow, AgmMarker } from '@agm/core';

import { GoogleService } from '../../google/google.service';
import PlaceResult = google.maps.places.PlaceResult;

import { Airport } from '../models/airport.model';
import { PromiseState, StatefulPromise } from '../../util/stateful-promise';
import { PlaceResultWrapper } from '../../google/place-result-wrapper';
import { AgmSnazzyInfoWindow } from '@agm/snazzy-info-window';


@Component({
  selector: 'app-local-activities',
  templateUrl: './local-activities.component.html',
  styleUrls: ['./local-activities.component.scss']
})
export class LocalActivitiesComponent implements AfterViewInit, OnInit {
  @Input() airport: Airport;
  @Input() tabType: string;
  @ViewChild('listEl') listEl: ElementRef;
  @ViewChildren('activityEl', { read: ElementRef }) activityCardEls: QueryList<ElementRef>;
  @ViewChild('airportMarker', { read: AgmMarker }) airportMarker: AgmMarker;
  @ViewChild('airportInfoWindow', { read: AgmInfoWindow }) airportInfoWindow: AgmInfoWindow;
  @ViewChildren('activityMarker', { read: AgmMarker }) activityMarkers: QueryList<AgmMarker>;
  @ViewChildren('activityInfoWindow', { read: AgmSnazzyInfoWindow }) activityInfoWindows: QueryList<AgmSnazzyInfoWindow>;
  latLngBounds: any;
  selectedActivity: PlaceResult;
  selectedType: string;
  activitiesPromise: StatefulPromise<PlaceResultWrapper>;
  private $containerEl;
  hoverTimer: any;

  constructor(private _google: GoogleService,
              private _zone: NgZone) { }

  onClickNextPage() {
    this.getRelatedPage('next');
  }

  onClickPrevPage() {
    this.getRelatedPage('previous');
  }

  async getRelatedPage(pageType: string) {
    if (pageType === 'previous') {
      this.activitiesPromise = new StatefulPromise(Promise.resolve(this.activitiesPromise.value.getPrevPage()));
    } else {
      this.activitiesPromise = new StatefulPromise(this.activitiesPromise.value.getNextPage());
    }
    this.activitiesPromise.promise.then((res) => {
      if (this.activities.length < 1) {
        return;
      }
      this.latLngBounds = new google.maps.LatLngBounds();
      this.activities.forEach((activity) => {
        this.latLngBounds.extend(new google.maps.LatLng(activity.geometry.location.lat(), activity.geometry.location.lng()));
      });
    });
  }

  onClickActivityType(activityType: string) {
    this.selectedType = activityType;
    this.getActivities();
  }

  async getActivities() {
    if (['fishing', 'skiing', 'golf'].includes(this.selectedType)) {
      this.activitiesPromise = new StatefulPromise(this._google.nearbySearch({
        rankBy: google.maps.places.RankBy.DISTANCE,
        keyword: this.selectedType,
        location: {lat: this.airport.location.lat, lng: this.airport.location.lon}
      }));
    } else if (this.selectedType === 'national-and-state-parks') {
      this.activitiesPromise = new StatefulPromise(this._google.nearbySearch({
        rankBy: google.maps.places.RankBy.DISTANCE,
        keyword: 'national and state parks',
        location: {lat: this.airport.location.lat, lng: this.airport.location.lon}
      }));
    } else {
      this.activitiesPromise = new StatefulPromise(this._google.nearbySearch({
        rankBy: google.maps.places.RankBy.DISTANCE,
        type: this.selectedType,
        location: { lat: this.airport.location.lat, lng: this.airport.location.lon }
      }));
    }
    this.latLngBounds = new google.maps.LatLngBounds();
    this.latLngBounds.extend(new google.maps.LatLng(this.airport.location.lat, this.airport.location.lon));
    this.activitiesPromise.promise.then((res) => {
      /* if (this.activities.length < 1) {
        return;
      } */
      this.activities.forEach((activity) => {
        this.latLngBounds.extend(new google.maps.LatLng(activity.geometry.location.lat(), activity.geometry.location.lng()));
      });
    });
  }

  get activities(): Array<PlaceResult> {
    if (!this.activitiesPromise || this.activitiesPromise.state !== PromiseState.Fulfilled) {
      return null;
    }
    return this.activitiesPromise.value.places;
  }

  get hasNextPage(): boolean {
    if (!this.activitiesPromise || this.activitiesPromise.state !== PromiseState.Fulfilled) {
      return false;
    }
    return this.activitiesPromise.value.hasNextPage;
  }

  get hasPrevPage(): boolean {
    if (!this.activitiesPromise || this.activitiesPromise.state !== PromiseState.Fulfilled) {
      return false;
    }
    return this.activitiesPromise.value.hasPrevPage;
  }

  ngOnInit() {
    if (this.tabType === 'lodging') {
      this.selectedType = 'lodging';
    } else if (this.tabType === 'restaurant') {
      this.selectedType = 'restaurant';
    } else {
      this.selectedType = 'national-and-state-parks';
    }
    // this.selectedType = 'park';
  }

  async ngAfterViewInit() {
    await this._google.promise;
    this.getActivities();
    this.$containerEl = $(this.listEl.nativeElement);
  }

  getIconUrl(activity: PlaceResult): string {
    return GoogleService.getIconUrl(activity, this.selectedType);
  }

  /**
   * Returns a jQuery element container for the given attraction.
   */
  getActivityEl(activity: PlaceResult) {
    const i = this.activities.indexOf(activity);
    if (i < 0) {
      return null;
    }
    const el = this.activityCardEls.toArray()[i];
    return $(el.nativeElement);
  }

  /**
   * Scrolls the attractions list to the specified attraction, marking it as selected.
   */
  public scrollToActivity(activity: PlaceResult) {
    const $el = this.getActivityEl(activity);
    if (!$el) {
      return;
    }
    const position = $el[0].offsetTop;
    this.$containerEl.stop(true);
    this.$containerEl.animate({ scrollTop: position }, 500);
  }

  /**
   * Hover handler for attraction cards in the list
   */
  public onActivityItemHover(activity: PlaceResult, hover: boolean) {
      if (hover) {
        this.selectedActivity = activity;
      }
  }

  onAirportMarkerClick() {
    this.infoWindowOpen(this.airportInfoWindow, this.airportMarker);
  }

  onAirportMarkerHover(hover: boolean) {
    if (hover) {
      this.infoWindowOpen(this.airportInfoWindow, this.airportMarker);
    } else {
      this.infoWindowClose(this.airportInfoWindow);
    }
  }

  onActivityMarkerHover(activity: PlaceResult, hover: boolean) {
    clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(() => {
        if (hover) {
          this.selectedActivity = activity;
          this.scrollToActivity(activity);
        }
      }, 400);
  }

  infoWindowOpen(infoWindow: AgmInfoWindow, marker: AgmMarker = null) {
    infoWindow.hostMarker = marker;
    // Run outside angular to avoid triggering an endless cycle of change detection:
    // https://github.com/SebastianM/angular-google-maps/issues/796
    this._zone.runOutsideAngular(() => {
      infoWindow.open();
    });
  }

  infoWindowClose(infoWindow: any) {
    this._zone.runOutsideAngular(() => {
      infoWindow.close();
    });
  }
}
